import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { tapResponse } from '@ngrx/operators';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  debounceTime,
  filter,
  forkJoin,
  groupBy,
  mergeMap,
  pipe,
  switchMap,
  tap,
} from 'rxjs';
import {
  Action,
  ActionPayload,
  ActionStatus,
  isOpen,
  isSettled,
} from '@maximizer/actions/domain';
import { ActionsState } from '../models/actions.model';
import { ACTIONS_API } from '../services/actions-api';

/**
 * How long a refresh group stays open after its last push. Long enough that a
 * burst for one action still lands in order, short enough that an id seen once
 * does not keep a group for the rest of the session.
 */
const REFRESH_COALESCE_MS = 10_000;

const initial: ActionsState = {
  actions: [],
  loading: false,
  loadError: null,
  error: null,
  payloadErrors: {},
  reviewRequests: 0,
};

/** JSON with object keys in a fixed order, so equal values read equal. */
const stable = (value: unknown): string =>
  JSON.stringify(value, (_, node: unknown) =>
    node && typeof node === 'object' && !Array.isArray(node)
      ? Object.fromEntries(
          Object.entries(node).sort(([a], [b]) => a.localeCompare(b)),
        )
      : node,
  );

/** What the server owns about an action; the rest is filled in client-side. */
const summaryOf = (action: Action): string =>
  stable({ ...action, payload: undefined, failureReason: undefined });

/**
 * Cards hold their unsaved edits against the action object they were given,
 * so any write into the store must hand back the very same object whenever
 * the server has nothing new to say about it. A fetched payload and a
 * refusal the server never stored both survive as long as the status has
 * not moved on.
 *
 * While this client's own approve or reject is in flight, whatever the
 * server says about that action is older than the call it has not answered
 * yet, so the local copy stands until that answer arrives.
 */
function reconcileOne(
  previous: Action | undefined,
  next: Action,
  inFlight = false,
): Action {
  if (!previous) {
    return next;
  }

  if (inFlight) {
    return previous;
  }

  const sameStatus = previous.status === next.status;
  const payload =
    next.payload == null && sameStatus ? previous.payload : next.payload;
  const failureReason =
    next.failureReason ?? (sameStatus ? previous.failureReason : undefined);

  const merged =
    payload === next.payload && failureReason === next.failureReason
      ? next
      : { ...next, payload, failureReason };

  return summaryOf(previous) === summaryOf(merged) &&
    previous.failureReason === merged.failureReason &&
    (previous.payload === merged.payload ||
      stable(previous.payload) === stable(merged.payload))
    ? previous
    : merged;
}

function reconcile(
  known: readonly Action[],
  loaded: readonly Action[],
  inFlight: ReadonlySet<string>,
): readonly Action[] {
  const byId = new Map(known.map((action) => [action.id, action]));

  return loaded.map((action) =>
    reconcileOne(byId.get(action.id), action, inFlight.has(action.id)),
  );
}

/** The server checked the request and declined it without executing. */
const refused = (error: unknown): boolean =>
  error instanceof HttpErrorResponse &&
  error.status >= 400 &&
  error.status < 500;

export const ActionsStore = signalStore(
  withState(initial),
  withComputed(({ actions }) => {
    const pending = computed(() =>
      actions().filter((action) => isOpen(action.status)),
    );
    const done = computed(() =>
      actions().filter((action) => isSettled(action.status)),
    );
    const waiting = computed(() =>
      actions().filter((action) => action.status === ActionStatus.Pending),
    );

    return {
      pending,
      done,
      doneCount: computed(() => done().length),
      waitingCount: computed(() => waiting().length),
      clientCount: computed(
        () => new Set(waiting().map((action) => action.client.id)).size,
      ),
    };
  }),
  withMethods((store) => {
    const api = inject(ACTIONS_API);

    /** Actions this client has an approve or reject running for. */
    const inFlight = new Set<string>();

    /** Actions written since the running load was sent, so newer than it. */
    const settledDuringLoad = new Set<string>();

    const mark = (id: string, patch: Partial<Action>): void =>
      patchState(store, {
        actions: store
          .actions()
          .map((action) =>
            action.id === id ? { ...action, ...patch } : action,
          ),
      });

    // The same object when there is nothing to drop, so a push for an
    // action without an error leaves every reader of the errors alone.
    const withoutPayloadError = (id: string): ActionsState['payloadErrors'] => {
      const errors = store.payloadErrors();
      return errors[id] === undefined
        ? errors
        : Object.fromEntries(
            Object.entries(errors).filter(([key]) => key !== id),
          );
    };

    const settle = (next: Action): void => {
      const previous = store.actions().find((action) => action.id === next.id);
      const merged = reconcileOne(previous, next, inFlight.has(next.id));

      if (store.loading()) {
        settledDuringLoad.add(next.id);
      }

      patchState(store, {
        actions: previous
          ? store
              .actions()
              .map((action) => (action.id === next.id ? merged : action))
          : [next, ...store.actions()],
        error: null,
        payloadErrors: withoutPayloadError(next.id),
      });
    };

    /**
     * The API envelopes every error as `{ error: string }`, so the message the
     * advisor should read sits at `error.error.error`. Without this the whole
     * HttpErrorResponse is stringified and a refusal as specific as "the
     * execute block of a row is not editable" reads as object noise.
     */
    const reason = (error: unknown): string => {
      const body = (error as { error?: { error?: unknown } } | null)?.error;

      if (typeof body?.error === 'string') {
        return body.error;
      }

      return error instanceof Error || error instanceof HttpErrorResponse
        ? error.message
        : String(error);
    };

    /**
     * A refusal leaves the action pending on the server, so it stays pending
     * here too and carries the reason for the advisor to act on. Anything
     * else may or may not have executed; the card is marked failed until the
     * server says otherwise.
     */
    const fail = (id: string, error: unknown): void => {
      const message = reason(error);

      inFlight.delete(id);
      mark(id, {
        status: refused(error) ? ActionStatus.Pending : ActionStatus.Failed,
        failureReason: message,
      });
      patchState(store, { error: message });
    };

    /** The answer to this client's own approve or reject: always authoritative. */
    const conclude = (next: Action): void => {
      inFlight.delete(next.id);
      settle(next);
    };

    // Parallel across actions, and torn down with the store: a decision still
    // in flight when the store goes away must not settle into it.
    const runApprove = rxMethod<{ id: string; payload: ActionPayload }>(
      mergeMap(({ id, payload }) =>
        api.apply(id, payload).pipe(
          tapResponse({
            next: conclude,
            error: (error: unknown) => fail(id, error),
          }),
        ),
      ),
    );

    const runReject = rxMethod<{ id: string; why?: string }>(
      mergeMap(({ id, why }) =>
        api.reject(id, why).pipe(
          tapResponse({
            next: conclude,
            error: (error: unknown) => fail(id, error),
          }),
        ),
      ),
    );

    return {
      // The host and the inbox both ask on startup; whoever is second joins
      // the load already running rather than racing it.
      load: rxMethod<void>(
        pipe(
          filter(() => !store.loading()),
          tap(() => {
            settledDuringLoad.clear();
            patchState(store, { loading: true, loadError: null, error: null });
          }),
          switchMap(() =>
            forkJoin([
              api.list(ActionStatus.Pending),
              api.list(ActionStatus.Done),
            ]).pipe(
              tapResponse({
                next: ([pending, done]) => {
                  const loaded = [...pending, ...done];
                  const ids = new Set(loaded.map((action) => action.id));
                  const had = new Set(
                    store.actions().map((action) => action.id),
                  );
                  const newer = store
                    .actions()
                    .filter(
                      (action) =>
                        settledDuringLoad.has(action.id) && !ids.has(action.id),
                    );
                  const held = new Set([...inFlight, ...settledDuringLoad]);

                  settledDuringLoad.clear();
                  patchState(store, {
                    actions: [
                      ...newer,
                      ...reconcile(store.actions(), loaded, held),
                    ],
                    loading: false,
                    // An action the store never had was asked for by id, and
                    // keeps its error until it is asked for again.
                    payloadErrors: Object.fromEntries(
                      Object.entries(store.payloadErrors()).filter(
                        ([id]) => ids.has(id) || !had.has(id),
                      ),
                    ),
                  });
                },
                error: (error: unknown) => {
                  settledDuringLoad.clear();
                  patchState(store, {
                    loadError: reason(error),
                    error: reason(error),
                    loading: false,
                  });
                },
              }),
            ),
          ),
        ),
      ),

      expand: rxMethod<string>(
        pipe(
          filter((id) => {
            const known = store.actions().find((action) => action.id === id);
            return known !== undefined && known.payload == null;
          }),
          tap((id) =>
            patchState(store, { payloadErrors: withoutPayloadError(id) }),
          ),
          mergeMap((id) =>
            api.get(id).pipe(
              tapResponse({
                next: settle,
                error: (error: unknown) =>
                  patchState(store, {
                    payloadErrors: {
                      ...store.payloadErrors(),
                      [id]: reason(error),
                    },
                  }),
              }),
            ),
          ),
        ),
      ),

      // Parallel across actions, latest-wins within one: three pushes for the
      // same id in quick succession must not settle out of order.
      refresh: rxMethod<string>(
        pipe(
          tap((id) =>
            patchState(store, { payloadErrors: withoutPayloadError(id) }),
          ),
          groupBy((id) => id, {
            duration: (sameId) =>
              sameId.pipe(debounceTime(REFRESH_COALESCE_MS)),
          }),
          mergeMap((sameId) =>
            sameId.pipe(
              switchMap((id) =>
                api.get(id).pipe(
                  tapResponse({
                    next: settle,
                    // Kept by id as well: an action the store never had can
                    // only show it where it was asked for.
                    error: (error: unknown) =>
                      patchState(store, {
                        error: reason(error),
                        payloadErrors: {
                          ...store.payloadErrors(),
                          [id]: reason(error),
                        },
                      }),
                  }),
                ),
              ),
            ),
          ),
        ),
      ),

      approve(id: string, payload: ActionPayload): void {
        inFlight.add(id);
        mark(id, {
          status: ActionStatus.Executing,
          payload,
          failureReason: undefined,
        });
        runApprove({ id, payload });
      },

      reject(id: string, why?: string): void {
        inFlight.add(id);
        mark(id, { status: ActionStatus.Executing, failureReason: undefined });
        runReject({ id, why });
      },

      showReview(): void {
        patchState(store, { reviewRequests: store.reviewRequests() + 1 });
      },
    };
  }),
);
